import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  DialogActions,
  Icon,
  Grid,
  CircularProgress,
} from '@material-ui/core'
import * as R from 'ramda'
import { useIntl, FormattedMessage } from 'react-intl'
import PreviewVoucherCard from './PreviewVoucherCard'
import { makeStyles } from '@material-ui/styles'
import ErrorBoundary from 'app/components/ErrorBoundary'
import ErrorView from 'app/components/ErrorView'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import { useBreakpoints } from 'commons/useBreakpoints'
import SecondaryButton from 'app/components/buttons/SecondaryButton'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import Formsy from 'formsy-react'
import { SelectFormsy, FuseAnimateGroup } from '@fuse'
import effects from 'app/effects/effects'
import {
  startFullScreenLoading,
  endFullScreenLoading,
} from 'app/store/actions/loadSpinner.actions'
import { useFetcher } from 'libs/fetcher'
import { useDispatch } from 'react-redux'
import Loader from 'app/components/Loader'
import useVoucherViewer from './useVoucherViewer'
import { LanguageSubscriberCount } from 'app/components/selectOptional'
import { useLanguagePreview } from 'commons/useLanguagePreview'
import ErrorTranslationMessage from 'app/components/ErrorTranslationMessage'

const useStyles = makeStyles((theme) => ({
  classClose: {
    textAlign: 'center',
    fontSize: '20px',
    cursor: 'pointer',
  },
  iconSize: {
    fontSize: '22px',
    color: '#000000',
    fontWeight: 'bold',
  },
  dialogPaper: {
    width: '500px !important',
    [useBreakpoints(theme).lessThan.sm]: {
      height: '100%',
      maxHeight: '100%',
      margin: '0px',
    },
  },
}))

const PreviewVoucher = ({
  vouchers,
  open,
  onClose,
  submitLabelId,
  isSubmitButtonDisabled,
  onClickSendNow,
  onChange,
  templateId,
}) => {
  const {
    templateView,
    getVoucherViewer,
    isLoading: isVoucherViewerLoading,
  } = useVoucherViewer()
  const classes = useStyles()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const defaultLanguage = useDefaultLanguage()
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage.id)
  const [isTranslateLoading, setIsTranslateLoading] = useState(false)
  const [translateError, setTranslateError] = useState(null)
  const [shownLanguages, setShownLanguages] = useState([])
  const [detectedLanguage, setDetectedLanguage] = useState(null)
  const [translatedVoucher, setTranslatedVoucher] = useState({
    ...vouchers,
  })

  const apiFetchersCallback = useCallback(
    () => effects.apis.getAllPlaceLanguage({ isAllPlaceLanguage: true }),
    [],
  )
  const { values: subscriberLanguage, isLoading: isLanguageLoading } =
    useFetcher(apiFetchersCallback, [])
  const { detectLanguage, sortLanguage } = useLanguagePreview(
    vouchers,
    subscriberLanguage,
    defaultLanguage,
  )

  const translate = (language) => {
    const detectedLanguage = R.equals(language, detectLanguage.id)
    setTranslateError(null)
    if (!detectedLanguage) {
      setIsTranslateLoading(true)
      effects.apis
        .translate({
          data: {
            languageId: language,
            title: vouchers.title,
            body: vouchers.body,
            description: vouchers.description,
          },
        })
        .then((result) => {
          const translated = {
            title: result.title,
            body: result.body,
            description: result.description,
          }
          getVoucherViewer({ vouchers: translated, templateId })
          setIsTranslateLoading(false)
          translateVoucher(translated)
        })
        .catch((error) => {
          setIsTranslateLoading(false)
          setTranslateError(error)
        })
    } else {
      const translated = {
        title: vouchers.title,
        body: vouchers.body,
        description: vouchers.description,
      }
      getVoucherViewer({ vouchers: translated, templateId })
      if (!R.isNil(translated) && !R.isEmpty(translated)) {
        translateVoucher(translated)
      }
    }
  }

  const translateVoucher = (translatedContent) => {
    setTranslatedVoucher(
      R.mergeRight(vouchers, {
        languageId: translatedContent.languageId,
        title: translatedContent.title,
        body: translatedContent.body,
        description: translatedContent.description,
      }),
    )
  }

  useEffect(() => {
    dispatch(
      isLanguageLoading ? startFullScreenLoading() : endFullScreenLoading(),
    )
  }, [dispatch, isLanguageLoading])

  useEffect(() => {
    getVoucherViewer({ vouchers, templateId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!sortLanguage) {
      return
    }
    setShownLanguages(sortLanguage)
    setDetectedLanguage(detectLanguage.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectLanguage, sortLanguage])

  if (isLanguageLoading) return null

  return (
    <div>
      <Dialog
        open={open}
        onClose={onClose}
        disableBackdropClick={true}
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <DialogTitle className="pb-4 mt-8">
          <Grid container spacing={2}>
            <Grid item xs={11}>
              <FormattedMessage id="preview.voucher.dialog.title" />
            </Grid>
            <Grid item xs={1} className={classes.classClose} onClick={onClose}>
              <Icon className={classes.iconSize} color="action">
                close
              </Icon>
            </Grid>
          </Grid>
        </DialogTitle>
        <ErrorBoundary fallback={<ErrorView />}>
          <DialogContent className="pt-4">
            <Formsy>
              <SelectFormsy
                className="w-full mt-16"
                name="translation"
                label={formatMessage({
                  id: 'translationPreview.translationSelectLabel',
                })}
                value={detectedLanguage || defaultLanguage.id}
                onChange={(event) => {
                  setSelectedLanguage(event.target.value)
                  translate(event.target.value)
                }}
                variant="outlined"
              >
                {shownLanguages.map((language) => (
                  <MenuItem key={language.id} value={language.id}>
                    <LanguageSubscriberCount
                      name={language.name}
                      nativeName={language.nativeName}
                    />
                  </MenuItem>
                ))}
              </SelectFormsy>
            </Formsy>

            <div className="w-full flex justify-center py-10">
              <Loader
                isLoading={isTranslateLoading || isVoucherViewerLoading}
                placeholder={
                  <FuseAnimateGroup
                    enter={{ animation: 'fadeIn' }}
                    leave={{ animation: 'fadeOut' }}
                  >
                    <div className="flex py-64">
                      <CircularProgress color="secondary" />
                    </div>
                  </FuseAnimateGroup>
                }
              >
                {R.isNil(translateError) ? (
                  <PreviewVoucherCard
                    vouchers={translatedVoucher}
                    templateView={templateView}
                    isVoucherViewerLoading={isVoucherViewerLoading}
                  />
                ) : (
                  <FuseAnimateGroup
                    enter={{ animation: 'transition.expandIn' }}
                    leave={{ animation: 'transition.expandOut' }}
                  >
                    <ErrorTranslationMessage />
                  </FuseAnimateGroup>
                )}
              </Loader>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="w-full flex justify-between sm:justify-end pb-8 px-16">
              <SecondaryButton
                className="flex self-center w-1/2 sm:w-auto sm:ltr:ml-16 rtl:mr-16 ltr:mr-4 rtl:ml-4 sm:ltr:mr-8 rtl:ml-8"
                onClick={onClose}
              >
                <FormattedMessage id="translationPreview.backButton" />
              </SecondaryButton>
              <PrimaryButton
                className="flex self-center w-1/2 sm:w-auto ltr:ml-4 rtl:mr-4 sm:ltr:ml-8 rtl:mr-8"
                onClick={onClickSendNow}
                disabled={isSubmitButtonDisabled}
              >
                {<FormattedMessage id={submitLabelId} />}
              </PrimaryButton>
            </div>
          </DialogActions>
        </ErrorBoundary>
      </Dialog>
    </div>
  )
}

export default PreviewVoucher
